/*
 * Author: Chao.Wang
 * File Created: Friday, 25-01-10 09:44 am
 * Last Modified: Thursday, 25-03-06 11:11 am, by Ben Zhao
 */
import { produce } from 'immer';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { createStoreActions } from '@web/utils';

import type { Agreement, AGREEMENT_TYPE } from '@/@types/agreement';
import { getAgreement } from '@/graphql/agreement';
import { confirmAgreement } from '@/services/account/service';

type AgreementWithStatus = Agreement & { confirmStatus: boolean };

interface AgreementState {
  /** 协议信息 */
  agreementInfo: Record<
    AGREEMENT_TYPE,
    Partial<AgreementWithStatus> | undefined
  >;
}

const initAgreementInfo = {
  TERMS_CONDITIONS: {},
  PRIVACY_POLICY: {},
};

const defaultState: AgreementState = {
  agreementInfo: initAgreementInfo,
};

interface AgreementAction {
  getAgreementInfo: (
    _typeList: AGREEMENT_TYPE[],
  ) => Promise<Partial<Record<AGREEMENT_TYPE, Partial<AgreementWithStatus>>>>;
  signAgreement: (_type: AGREEMENT_TYPE[]) => Promise<void>;
  clearData: () => void;
  updateAgreement: (
    _type: AGREEMENT_TYPE,
    _info: Partial<AgreementWithStatus>,
  ) => void;
}

const agreementActions = createStoreActions<AgreementState, AgreementAction>(
  (set, get) => ({
    clearData: () => {
      const typeList = Object.keys(get().agreementInfo) as AGREEMENT_TYPE[];

      typeList.forEach((type) => {
        get().updateAgreement(type, {
          confirmStatus: undefined,
        });
      });
    },

    getAgreementInfo: async (typeList) => {
      const agreements: Partial<
        Record<AGREEMENT_TYPE, Partial<AgreementWithStatus>>
      > = {};
      const results = await Promise.all(
        typeList.map(async (type) => {
          const force = Boolean(!get().agreementInfo[type]?.version);
          const info = await getAgreement({ type }, { force });

          return { type, ...info };
        }),
      );

      results.forEach((info) => {
        if (info.version) {
          set(
            produce((state: AgreementState) => {
              state.agreementInfo[info.type] = { ...info };
              agreements[info.type] = { ...info };
            }),
          );
        }
      });

      return agreements;
    },

    updateAgreement: (type, info) => {
      set(
        produce((state: AgreementState) => {
          state.agreementInfo[type] = {
            ...get().agreementInfo[type],
            ...info,
          };
        }),
      );
    },

    signAgreement: async (typeList) => {
      const params = typeList
        .map((type) => ({
          type,
          version: get().agreementInfo[type]?.version,
          url: get().agreementInfo[type]?.url,
        }))
        .filter((item) => item.version);

      await confirmAgreement(params);
      typeList.forEach((type) => {
        get().updateAgreement(type, { confirmStatus: true });
      });
    },
  }),
);

const useAgreementStore = create<AgreementState & AgreementAction>()(
  (set, get) => ({
    ...defaultState,
    ...agreementActions(set, get),
  }),
);

export const useAgreement = () => {
  const {
    agreementInfo,
    signAgreement,
    getAgreementInfo,
    clearData,
    updateAgreement,
  } = useAgreementStore(
    useShallow((state) => ({
      agreementInfo: state.agreementInfo,
      signAgreement: state.signAgreement,
      getAgreementInfo: state.getAgreementInfo,
      clearData: state.clearData,
      updateAgreement: state.updateAgreement,
    })),
  );

  return {
    agreementInfo,
    signAgreement,
    getAgreementInfo,
    clearData,
    updateAgreement,
  };
};

export const useAgreementActions = () => {
  const { signAgreement, getAgreementInfo, clearData, updateAgreement } =
    useAgreementStore(
      useShallow((state) => ({
        signAgreement: state.signAgreement,
        getAgreementInfo: state.getAgreementInfo,
        clearData: state.clearData,
        updateAgreement: state.updateAgreement,
      })),
    );

  return {
    signAgreement,
    getAgreementInfo,
    clearData,
    updateAgreement,
  };
};
